/**
 * Bold Text Processor
 *
 * Single Responsibility: Processes bold markdown (**text**) and italic markdown (*text*)
 * Dependency Inversion Principle: Depends on ITextProcessor abstraction
 */

import React from 'react';
import { typography } from '@/design-system/utils';
import { ITextProcessor, TextProcessorOptions } from './ITextProcessor';
import { PlaceLinkProcessor } from './PlaceLinkProcessor';

export class BoldTextProcessor implements ITextProcessor {
  private placeLinkProcessor: ITextProcessor;

  constructor(placeLinkProcessor?: ITextProcessor) {
    this.placeLinkProcessor = placeLinkProcessor || new PlaceLinkProcessor();
  }

  process(
    text: string,
    keyPrefix: string,
    options?: TextProcessorOptions
  ): React.ReactNode[] {
    const parts: React.ReactNode[] = [];

    // Find all matches for both bold (**text**) and italic (*text*)
    // Bold takes priority, so we process it first
    const matches: Array<{
      index: number;
      length: number;
      type: 'bold' | 'italic';
      content: string;
    }> = [];

    // Find all bold matches first (takes priority)
    const boldRegex = /\*\*([^*]+?)\*\*/g;
    let match;
    while ((match = boldRegex.exec(text)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        type: 'bold',
        content: match[1],
      });
    }

    // Find all italic matches, but skip if they overlap with bold
    // Match *text* but ensure it's not part of **text**
    const italicRegex = /\*([^*]+?)\*/g;
    while ((match = italicRegex.exec(text)) !== null) {
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;

      // Check if this italic match is actually part of a bold match
      const isPartOfBold = matches.some(
        m =>
          m.type === 'bold' &&
          matchStart >= m.index - 1 &&
          matchEnd <= m.index + m.length + 1
      );

      if (!isPartOfBold) {
        matches.push({
          index: match.index,
          length: match[0].length,
          type: 'italic',
          content: match[1],
        });
      }
    }

    // Sort matches by index
    matches.sort((a, b) => a.index - b.index);

    let lastIndex = 0;

    for (const match of matches) {
      // Add text before the match
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index);
        const beforeParts = this.placeLinkProcessor.process(
          beforeText,
          `${keyPrefix}-before-${lastIndex}`,
          options
        );
        parts.push(...beforeParts);
      }

      // Process the matched content (bold or italic)
      const contentParts = this.placeLinkProcessor.process(
        match.content,
        `${keyPrefix}-${match.type}-${match.index}`,
        options
      );

      // Wrap in appropriate tag
      if (match.type === 'bold') {
        parts.push(
          <strong
            key={`${keyPrefix}-bold-${match.index}`}
            style={{
              fontWeight: typography.weight.bold,
            }}
          >
            {contentParts}
          </strong>
        );
      } else {
        parts.push(
          <em
            key={`${keyPrefix}-italic-${match.index}`}
            style={{
              fontStyle: 'italic',
            }}
          >
            {contentParts}
          </em>
        );
      }

      lastIndex = match.index + match.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      const remainingParts = this.placeLinkProcessor.process(
        remainingText,
        `${keyPrefix}-end-${lastIndex}`,
        options
      );
      parts.push(...remainingParts);
    }

    // If no parts were created (no bold or italic text found), delegate to placeLinkProcessor
    if (parts.length === 0) {
      return this.placeLinkProcessor.process(text, keyPrefix, options);
    }

    return parts;
  }
}
